import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

type ScrollDirection = 'prev' | 'next';

interface UseScrollNavigationReturn {
  readonly listRef: RefObject<HTMLDivElement | null>;
  readonly canScrollLeft: boolean;
  readonly canScrollRight: boolean;
  readonly scroll: (direction: ScrollDirection) => void;
}

export const useScrollNavigation = (): UseScrollNavigationReturn => {
  const listRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const updateScrollState = () => {
      const { scrollLeft, scrollWidth, clientWidth } = list;
      setCanScrollLeft(scrollLeft > 5);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5);
    };

    list.addEventListener('scroll', updateScrollState, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(list);
    updateScrollState();

    return () => {
      list.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, []);

  const scroll = useCallback((direction: ScrollDirection) => {
    const listRefCurrent = listRef.current;
    if (!listRefCurrent) return;

    const itemWidth = (listRefCurrent.children[0] as HTMLElement)?.offsetWidth || 0;
    listRefCurrent.scrollBy({ left: direction === 'prev' ? -itemWidth : itemWidth, behavior: 'smooth' });
  }, []);

  return { listRef, canScrollLeft, canScrollRight, scroll };
};
